import React, {
  useContext,
  useState,
  useRef,
  useLayoutEffect,
  useEffect
} from 'react';
import { useHistory } from 'react-router-dom';
import { APP_URL } from 'types';
import { LocalStoreContext } from '../../../localStore/context';
import {
  Link,
  Button,
  CodeSnippet,
  InlineNotification
} from 'carbon-components-react';
import ProgressBar from 'carbon-components-react/lib/components/ProgressBar';
import { Connect16, Add16 } from '@carbon/icons-react';
import { zoomToFeature } from 'components/Map/helper';
import {
  toLocalDateFormat,
  formFullAddress,
  toLocalDateTimeFormat
} from 'utilities/helper.util';
import { useEventListener } from 'utilities/hooks.util';

const generateSingleResult = (enquiries: any[]) => {
  if (enquiries.length > 0) {
    const text = `Job: ${enquiries[0].external_id}
Job dates: ${toLocalDateFormat(enquiries[0].start_at)} → ${toLocalDateFormat(
      enquiries[0].end_at
    )}
My reference: ${enquiries[0].user_reference || 'N/A'}
Job site: ${formFullAddress(enquiries[0].address)}
Enquiry date: ${toLocalDateTimeFormat(enquiries[0].enquiry_at)}`;
    return text;
  } else {
    return '';
  }
};

const generateMultiResult = (enquiries: any[]) => {
  if (enquiries.length === 0) {
    return 'Your summary will appear here';
  } else {
    const text = `Job dates: ${toLocalDateFormat(
      enquiries[0].start_at
    )} → ${toLocalDateFormat(enquiries[0].end_at)}
Job site: Multiple addresses
Enquiry date: ${toLocalDateTimeFormat(enquiries[0].enquiry_at)}
*****
${enquiries.map(
  (enquiry) => `Job: ${enquiry.external_id}
My reference: ${enquiry.user_reference || 'N/A'}`
).join(`
*****
`)}`;
    return text;
  }
};

const zoomToFeatureByMapId = (mapId: number) => {
  const { loadedMap } = window;
  if (loadedMap) {
    const drawingLayer = loadedMap.findLayerById('drawing-layer');
    if (drawingLayer) {
      const graphic = drawingLayer.graphics.items.find((g: any) => {
        return g.uid === mapId;
      });
      if (graphic) {
        zoomToFeature(graphic.geometry.extent, 0, true);
      }
    }
  }
};

const SuccessfulLodgedForm: React.FC<Props> = ({
  completeEnquiryHandler,
  retryFailedEnquiries
}) => {
  const history = useHistory();
  const localStore = useContext(LocalStoreContext);
  const [codeSnippetRowNumber, setCodeSnippetRowNumber] = useState<number>();
  const [showFailedDetails, setShowFailedDetails] = useState<boolean>(false);
  const [showCompletedDetails, setShowCompletedDetails] =
    useState<boolean>(true);
  const [disableViewThisEnquiryLink, setDisableViewThisEnquiryLink] =
    useState<boolean>(true);
  const headerContainerRef = useRef<HTMLDivElement>(null);
  const fooderContainerRef = useRef<HTMLDivElement>(null);
  const {
    enquiries,
    bulkCreating,
    shapes,
    pendingEnquiriesAmount,
    failedEnquiries,
    bulkBatchTotal = 0,
    bulkBatchCreated = 0
  } = localStore;

  const handleResize = () => {
    const windowsHeight =
      document.documentElement?.clientHeight || window.innerHeight;
    const headerHeight = headerContainerRef.current?.offsetHeight || 0;
    const footHeight = fooderContainerRef.current?.offsetHeight || 0;
    let offset = 10;
    if (headerHeight > 0) {
      offset += 1;
    }
    if (footHeight > 0) {
      offset += 1;
    }
    const height = windowsHeight - (headerHeight + footHeight);
    const row = Math.floor(height / 16) - offset;
    setCodeSnippetRowNumber(row);
    const snippetContainer = document.getElementsByClassName(
      'bx--snippet-container'
    );
    if (snippetContainer[0]) {
      snippetContainer[0].scrollTop = snippetContainer[0].scrollHeight;
    }
  };

  useEventListener('resize', handleResize, window);

  useLayoutEffect(() => {
    handleResize();
    setTimeout(() => handleResize(), 100);
  });

  useEffect(() => {
    if (enquiries.length > 0) {
      setTimeout(() => {
        setDisableViewThisEnquiryLink(false);
      }, 3000);
    }
  }, [enquiries]);

  return (
    <div className="enquiry-form--successful">
      <div
        ref={headerContainerRef}
        className={showFailedDetails ? 'show-fail-detail' : ''}
      >
        {bulkCreating && (
          <div className="in-progress-container">
            <ProgressBar
              label={`Lodging ${
                bulkBatchCreated + 1 > bulkBatchTotal
                  ? bulkBatchCreated
                  : bulkBatchCreated + 1
              }/${bulkBatchTotal} ${
                bulkBatchTotal > 1 ? 'enquiries' : 'enquiry'
              }`}
              helperText="please wait ..."
              max={bulkBatchCreated > 0 ? bulkBatchTotal : undefined}
              value={bulkBatchCreated > 0 ? bulkBatchCreated : undefined}
            />
          </div>
        )}
        {!bulkCreating && shapes.length > 1 && failedEnquiries.length > 0 && (
          <>
            <InlineNotification
              title={`${failedEnquiries.length} Failed ${
                failedEnquiries.length > 1 ? 'enquiries' : 'enquiry'
              }`}
              kind="error"
              lowContrast
              hideCloseButton
              actions={
                <>
                  <Button
                    kind="ghost"
                    size="sm"
                    className="retry-btn"
                    onClick={() => retryFailedEnquiries()}
                  >
                    Retry
                  </Button>
                  <Button
                    kind="ghost"
                    size="sm"
                    className="detail-btn"
                    onClick={() => {
                      setShowFailedDetails(!showFailedDetails);
                      setShowCompletedDetails(!!showFailedDetails);
                    }}
                  >
                    Details
                  </Button>
                </>
              }
            />
            {showFailedDetails && (
              <div
                className={`failed-enquiries bx--snippet bx--snippet--multi bx--snippet--wraptext${
                  enquiries.length > 0 ? ' has-success' : ''
                }`}
              >
                <div className="bx--snippet-container">
                  <pre>
                    <code>
                      {`Job dates: ${toLocalDateFormat(
                        failedEnquiries[0].start_at
                      )} → ${toLocalDateFormat(failedEnquiries[0].end_at)}
Job site: Multiple addresses
Enquiry date: ${toLocalDateTimeFormat(failedEnquiries[0].enquiry_at)}
*****
`}
                    </code>
                    {failedEnquiries.map((enquiry, inx) => (
                      <>
                        <code>
                          {`Error: ${enquiry.errorMsg}
`}
                        </code>
                        <Link
                          onClick={() => zoomToFeatureByMapId(enquiry.mapId)}
                        >
                          [Zoom to]
                        </Link>
                        {inx <= failedEnquiries.length - 2 &&
                          `
*****
`}
                      </>
                    ))}
                  </pre>
                </div>
              </div>
            )}
          </>
        )}
        {!bulkCreating && enquiries.length > 0 && (
          <InlineNotification
            title={
              shapes.length > 1
                ? `${enquiries.length} ${
                    enquiries.length === 1 ? 'Enquiry' : 'Enquiries'
                  } lodged`
                : 'Enquiry lodged'
            }
            kind="success"
            lowContrast
            hideCloseButton
            actions={
              shapes.length > 1 ? (
                <Button
                  kind="ghost"
                  size="sm"
                  className="detail-btn"
                  onClick={() => {
                    setShowFailedDetails(!!showCompletedDetails);
                    setShowCompletedDetails(!showCompletedDetails);
                  }}
                >
                  Details
                </Button>
              ) : undefined
            }
          />
        )}
      </div>
      {(bulkCreating || (showCompletedDetails && enquiries.length > 0)) && (
        <CodeSnippet
          type="multi"
          wrapText={true}
          maxCollapsedNumberOfRows={codeSnippetRowNumber}
          minCollapsedNumberOfRows={codeSnippetRowNumber}
        >
          {shapes.length > 1
            ? generateMultiResult(enquiries)
            : generateSingleResult(enquiries)}
        </CodeSnippet>
      )}
      <div ref={fooderContainerRef}>
        {!bulkCreating &&
          (enquiries.length > 0 || pendingEnquiriesAmount !== undefined) && (
            <>
              <div className="buttons-container">
                {shapes.length === 1 && (
                  <Button
                    renderIcon={Connect16}
                    kind="tertiary"
                    onClick={() =>
                      completeEnquiryHandler(enquiries[0].external_id)
                    }
                  >
                    Continue job
                  </Button>
                )}
                <Button
                  renderIcon={Add16}
                  kind="tertiary"
                  onClick={() => completeEnquiryHandler()}
                >
                  Lodge another
                </Button>
              </div>
              {shapes.length > 1 ? (
                <Link onClick={() => history.push(APP_URL.ROOT_URL)}>
                  View on search map
                </Link>
              ) : (
                <>
                  <div className="desc">
                    Use <span className="italic">Continue job</span> to create a
                    continuous dig site made up of multiple BYDA enquiries.{' '}
                    <span className="italic">Lodge another</span> begins a new,
                    independent BYDA enquiry starting with your default map
                    extent.
                  </div>
                  <Link
                    onClick={() =>
                      history.push({
                        pathname: APP_URL.ROOT_URL,
                        state: {
                          enquiryId: String(enquiries[0].id),
                          jobId: String(enquiries[0].external_id)
                        }
                      })
                    }
                    disabled={disableViewThisEnquiryLink}
                  >
                    View this enquiry
                  </Link>
                </>
              )}
            </>
          )}
      </div>
    </div>
  );
};

type Props = {
  completeEnquiryHandler: Function;
  retryFailedEnquiries: Function;
};

export default React.memo(SuccessfulLodgedForm);
